import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import styled from '@emotion/styled';
import * as clipboard from 'clipboard-polyfill';
import { Copy, Check } from 'lucide-react';
import { createOrder, startPayment } from 'actions/payment';
import { fetchAssessmentProduct } from 'actions/counselor';
import { getLanguage } from 'translation';
import { getBaseUrl } from 'utils/seo';
import getLocale from 'helpers/getLocale';
import { formatCurrencyDisplay } from 'helpers/currency';

const BODY_FONT = 'Rubik-Regular, system-ui, sans-serif';
// Fallback used only on catalog fetch failure (network down / bad config).
// The real price comes from `/assessments/products/assessment360` (pack
// `single`) at runtime so operators can change pricing without a deploy.
const FALLBACK_PRICE_UAH = 1499;
const FALLBACK_CURRENCY = 'UAH';
const ORDER_TYPE = 'assessment';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Heading = styled(Typography)({
  fontSize: 26,
  fontWeight: 700,
  textAlign: 'left',
  marginBottom: 12,
  lineHeight: 1.3,
});

const Lede = styled(Typography)(({ theme }) => ({
  fontFamily: BODY_FONT,
  fontSize: 15,
  color: theme.palette.grey[700],
  textAlign: 'left',
  marginBottom: 24,
  lineHeight: 1.55,
}));

const PriceBlock = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '28px 24px',
  borderRadius: 20,
  background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.pale} 100%)`,
  border: `1px solid ${theme.palette.primary.light}`,
  marginBottom: 28,
}));

const PriceLabel = styled('span')(({ theme }) => ({
  fontFamily: BODY_FONT,
  fontSize: 12,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: 1.4,
  color: theme.palette.grey[600],
  marginBottom: 6,
}));

const PriceValue = styled('span')(({ theme }) => ({
  fontFamily: BODY_FONT,
  fontSize: 44,
  fontWeight: 800,
  letterSpacing: -1,
  color: theme.palette.primary.main,
  lineHeight: 1,
}));

const EmailInput = styled(TextField)({
  backgroundColor: '#fff',
  marginBottom: 8,
  '& .MuiOutlinedInput-root': {
    fontFamily: BODY_FONT,
    borderRadius: 14,
    fontSize: 16,
  },
  '& .MuiInputLabel-root': {
    fontFamily: BODY_FONT,
  },
  '& .MuiInputLabel-root.MuiInputLabel-shrink': {
    backgroundColor: '#fff',
    padding: '0 6px',
    transform: 'translate(12px, -9px) scale(0.75)',
  },
});

const Hint = styled(Typography)(({ theme }) => ({
  fontFamily: BODY_FONT,
  fontSize: 13,
  color: theme.palette.grey[600],
  marginBottom: 24,
  lineHeight: 1.5,
}));

const ErrorBanner = styled('div')(({ theme }) => ({
  fontFamily: BODY_FONT,
  backgroundColor: '#fdecea',
  border: `1px solid ${theme.palette.error.light}`,
  color: theme.palette.error.dark,
  padding: '12px 16px',
  borderRadius: 12,
  fontSize: 14,
  lineHeight: 1.5,
  marginBottom: 16,
}));

const ActionRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  flexWrap: 'wrap',
}));

const PrimaryButton = styled(Button)({
  fontFamily: BODY_FONT,
  borderRadius: 25,
  padding: '14px 32px',
  fontSize: 16,
  fontWeight: 600,
  textTransform: 'none',
  width: 240,
  flexShrink: 0,
});

const CopyLink = styled('button')(({ theme }) => ({
  background: 'none',
  border: 'none',
  padding: '6px 0',
  fontFamily: BODY_FONT,
  fontSize: 13,
  color: theme.palette.grey[300],
  textDecoration: 'underline',
  textUnderlineOffset: 3,
  textAlign: 'left',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  '&:hover': { color: theme.palette.grey[500] },
  '&:disabled': { opacity: 0.6, cursor: 'default' },
}));

const Copied = styled('span')(({ theme }) => ({
  fontFamily: BODY_FONT,
  fontSize: 13,
  color: theme.palette.primary.main,
  marginTop: 8,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
}));

const FinePrint = styled(Typography)(({ theme }) => ({
  fontFamily: BODY_FONT,
  fontSize: 13,
  color: theme.palette.grey[600],
  marginTop: 18,
  lineHeight: 1.5,
}));

const MethodsBlock = styled('div')(({ theme }) => ({
  marginTop: 28,
  paddingTop: 20,
  borderTop: `1px solid ${theme.palette.grey[100]}`,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
}));

const MethodsLabel = styled('span')(({ theme }) => ({
  fontFamily: BODY_FONT,
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: 1.4,
  color: theme.palette.grey[500],
}));

const MethodsRow = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 24,
});

const MethodChip = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: 28,
});

const VisaLogo = () => (
  <svg width="64" height="22" viewBox="0 0 100 32" xmlns="http://www.w3.org/2000/svg" aria-label="Visa">
    <text x="0" y="26"
      fontFamily="'Arial Black', Arial, Helvetica, sans-serif"
      fontStyle="italic"
      fontWeight="900"
      fontSize="26"
      fill="#1A1F71"
      letterSpacing="-0.5">VISA</text>
  </svg>
);

const MastercardLogo = () => (
  <svg width="40" height="26" viewBox="0 0 40 26" xmlns="http://www.w3.org/2000/svg" aria-label="Mastercard">
    <circle cx="15" cy="13" r="11" fill="#EB001B"/>
    <circle cx="25" cy="13" r="11" fill="#F79E1B" style={{ mixBlendMode: 'multiply' }}/>
  </svg>
);

const MonobankLogo = () => (
  <svg width="92" height="20" viewBox="0 0 92 20" xmlns="http://www.w3.org/2000/svg" aria-label="monobank">
    <text x="0" y="15"
      fontFamily="'Helvetica Neue', Helvetica, Arial, sans-serif"
      fontWeight="800"
      fontSize="16"
      fill="#000"
      letterSpacing="-0.6">monobank</text>
  </svg>
);

const ApplePayLogo = () => (
  <svg width="58" height="22" viewBox="0 0 58 22" xmlns="http://www.w3.org/2000/svg" aria-label="Apple Pay">
    <path d="M11.06 5.92c-.56.66-1.46 1.18-2.36 1.1-.11-.9.33-1.86.84-2.46.56-.68 1.55-1.16 2.34-1.2.09.94-.27 1.86-.82 2.56zm.81 1.28c-1.31-.08-2.43.74-3.05.74-.63 0-1.58-.7-2.61-.68-1.34.02-2.59.78-3.27 1.98-1.4 2.42-.37 6 1 7.96.66.97 1.45 2.04 2.49 2 1-.04 1.39-.65 2.6-.65s1.56.65 2.61.63c1.09-.02 1.77-.97 2.43-1.94.76-1.1 1.07-2.18 1.09-2.24-.02-.02-2.1-.82-2.12-3.22-.02-2.01 1.64-2.97 1.71-3.02-.94-1.39-2.4-1.54-2.91-1.58z" fill="#000"/>
    <text x="20" y="17"
      fontFamily="'Helvetica Neue', Helvetica, Arial, sans-serif"
      fontWeight="600"
      fontSize="14"
      fill="#000"
      letterSpacing="-0.3">Pay</text>
  </svg>
);

const GooglePayLogo = () => (
  <svg width="92" height="22" viewBox="0 0 92 22" xmlns="http://www.w3.org/2000/svg" aria-label="Google Pay">
    <text x="0" y="17"
      fontFamily="'Helvetica Neue', Helvetica, Arial, sans-serif"
      fontWeight="600"
      fontSize="15"
      letterSpacing="-0.3">
      <tspan fill="#4285F4">G</tspan>
      <tspan fill="#EA4335">o</tspan>
      <tspan fill="#FBBC04">o</tspan>
      <tspan fill="#4285F4">g</tspan>
      <tspan fill="#34A853">l</tspan>
      <tspan fill="#EA4335">e</tspan>
      <tspan fill="#000" dx="3">Pay</tspan>
    </text>
  </svg>
);

export default function Purchase() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const user = useSelector((state) => state.app?.user);

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [copyPending, setCopyPending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  // Price lives in MongoDB (`assessment-products` → `assessment360`,
  // `single` pack). Falls back to the canonical 1499 UAH (matches the
  // seed) when the catalog is unreachable so the page never renders
  // blank pricing.
  const [priceUah, setPriceUah] = useState(FALLBACK_PRICE_UAH);
  const [currency, setCurrency] = useState(FALLBACK_CURRENCY);

  // Jobalto pays through Creem, not Monobank — hide the Monobank chip there.
  const isJobalto = React.useMemo(() => getBaseUrl().includes('jobalto.com'), []);

  // Prefill email for authenticated users.
  useEffect(() => {
    if (user?.email && !email) setEmail(user.email);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    dispatch(fetchAssessmentProduct('assessment360')).then((res) => {
      if (cancelled || !res?.data) return;
      const single = (res.data.packs || []).find((p) => p.id === 'single');
      if (single?.price?.amount) {
        setPriceUah(single.price.amount);
        setCurrency(single.price.currency || FALLBACK_CURRENCY);
      }
    });
    return () => { cancelled = true; };
  }, [dispatch]);

  const validateEmail = () => {
    const trimmed = email.trim();
    if (!EMAIL_RE.test(trimmed)) {
      setEmailError(true);
      return null;
    }
    return trimmed;
  };

  const buildPaymentLink = async (trimmed) => {
    const order = await dispatch(
      createOrder({
        type: ORDER_TYPE,
        options: {
          email: trimmed,
          sessionId: sessionStorage.getItem('sessionId') || null,
          additionalOrderInfo: {
            language: getLanguage(),
            // priceUAH is informational only — backend always owns the price
            // by resolving it from the catalog at order-creation time.
            priceUAH: priceUah,
          },
        },
      })
    );
    if (!order || order instanceof Error || !order._id) {
      throw new Error('order_create_failed');
    }

    const result = await dispatch(startPayment(order._id, order.guestToken));
    if (!result || result instanceof Error || !result.pageUrl) {
      throw new Error('payment_start_failed');
    }
    return result.pageUrl;
  };

  const handlePay = async (e) => {
    e?.preventDefault?.();
    if (submitting || copyPending) return;

    const trimmed = validateEmail();
    if (!trimmed) return;

    setSubmitting(true);
    setErrorMsg(null);

    try {
      const pageUrl = await buildPaymentLink(trimmed);
      window.location.href = pageUrl;
    } catch (err) {
      console.error('Assessment purchase failed:', err);
      setErrorMsg(t('Assessment.purchaseError'));
      setSubmitting(false);
    }
  };

  const handleCopyLink = async () => {
    if (submitting || copyPending) return;

    const trimmed = validateEmail();
    if (!trimmed) return;

    setCopyPending(true);
    setErrorMsg(null);

    try {
      const pageUrl = await buildPaymentLink(trimmed);
      await clipboard.writeText(pageUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      console.error('Copy payment link failed:', err);
      setErrorMsg(t('Assessment.purchaseError'));
    } finally {
      setCopyPending(false);
    }
  };

  return (
    <form onSubmit={handlePay} noValidate>
      <Heading variant="h1">{t('Assessment.purchaseHeading')}</Heading>
      <Lede>{t('Assessment.purchaseLede')}</Lede>

      <PriceBlock>
        <PriceLabel>{t('Assessment.purchasePriceLabel')}</PriceLabel>
        <PriceValue>{t('Assessment.purchasePrice', { amount: priceUah, currency: formatCurrencyDisplay(currency, getLocale()) })}</PriceValue>
      </PriceBlock>

      {errorMsg && <ErrorBanner role="alert">{errorMsg}</ErrorBanner>}

      <EmailInput
        fullWidth
        type="email"
        label={t('Assessment.purchaseEmailLabel')}
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
          if (emailError) setEmailError(false);
        }}
        placeholder={t('Assessment.purchaseEmailPlaceholder')}
        error={emailError}
        helperText={emailError ? t('Assessment.purchaseEmailInvalid') : ''}
        disabled={submitting || copyPending}
        slotProps={{ inputLabel: { shrink: true } }}
      />
      <Hint>{t('Assessment.purchaseEmailHint')}</Hint>

      <ActionRow>
        <PrimaryButton
          type="submit"
          variant="contained"
          color="primary"
          size="large"
          disabled={submitting || copyPending}
        >
          {submitting ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : t('Assessment.purchasePay')}
        </PrimaryButton>

        <CopyLink type="button" onClick={handleCopyLink} disabled={submitting || copyPending}>
          {copyPending ? <CircularProgress size={14} /> : <Copy size={14} />}
          {t('Assessment.purchaseCopyLink')}
        </CopyLink>
      </ActionRow>

      {copied && (
        <Copied>
          <Check size={14} />
          {t('Assessment.purchaseCopied')}
        </Copied>
      )}

      <FinePrint>{t('Assessment.purchaseFinePrint')}</FinePrint>

      <MethodsBlock>
        <MethodsLabel>{t('Assessment.purchaseAcceptedMethods')}</MethodsLabel>
        <MethodsRow>
          {!isJobalto && (
            <MethodChip><MonobankLogo /></MethodChip>
          )}
          <MethodChip><VisaLogo /></MethodChip>
          <MethodChip><MastercardLogo /></MethodChip>
          <MethodChip><ApplePayLogo /></MethodChip>
          <MethodChip><GooglePayLogo /></MethodChip>
        </MethodsRow>
      </MethodsBlock>
    </form>
  );
}
